import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function AdminForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/admin/forgot-password");
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Admin password reset" noindex />
      <div
        className="min-h-screen flex items-center justify-center px-5 py-10"
        style={{ background: "#F3F4F6" }}
        data-testid="admin-forgot-page"
      >
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white rounded-[28px] px-8 sm:px-10 py-12 text-center"
          style={{ boxShadow: "0 24px 60px -20px rgba(15, 23, 42, 0.18), 0 8px 20px -12px rgba(15, 23, 42, 0.10)" }}
        >
          {sent ? (
            <>
              <div
                className="mx-auto w-20 h-20 rounded-[22px] flex items-center justify-center text-white"
                style={{
                  background: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
                  boxShadow: "0 14px 32px -10px rgba(16, 185, 129, 0.45)",
                }}
              >
                <CheckCircle2 className="w-10 h-10" strokeWidth={2.5} />
              </div>
              <h1 className="mt-7 text-3xl font-extrabold tracking-tight text-slate-900">Check the inbox</h1>
              <p className="mt-3 text-slate-500 text-base leading-relaxed">
                If an admin account exists, we just sent a secure reset link to the operations inbox. It expires in <strong>15 minutes</strong>.
              </p>
              <Link
                to="/admin/login"
                data-testid="back-to-login"
                className="mt-10 w-full inline-flex items-center justify-center gap-2 h-14 rounded-full text-white text-base font-semibold transition-all"
                style={{ background: "#0F172A", boxShadow: "0 12px 28px -10px rgba(15, 23, 42, 0.45)" }}
              >
                <ArrowLeft className="w-5 h-5" /> Back to login
              </Link>
            </>
          ) : (
            <>
              <div
                className="mx-auto w-20 h-20 rounded-[22px] flex items-center justify-center text-white"
                style={{
                  background: "linear-gradient(135deg, #C026D3 0%, #DB2777 40%, #F97316 100%)",
                  boxShadow: "0 14px 32px -10px rgba(219, 39, 119, 0.55), 0 8px 16px -8px rgba(249, 115, 22, 0.45)",
                }}
              >
                <Mail className="w-9 h-9" strokeWidth={2.5} />
              </div>
              <h1 className="mt-7 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Forgot password?
              </h1>
              <p className="mt-3 text-slate-500 text-base leading-relaxed">
                We'll email a secure reset link to the operations inbox.
              </p>

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="mt-10 w-full inline-flex items-center justify-center gap-3 h-16 rounded-full text-white text-lg font-semibold transition-all disabled:opacity-60"
                style={{ background: "#0F172A", boxShadow: "0 12px 28px -10px rgba(15, 23, 42, 0.45)" }}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    Sending…
                  </span>
                ) : (
                  <>
                    <Mail className="w-5 h-5" /> Send reset link
                  </>
                )}
              </button>

              <div className="mt-5">
                <Link
                  to="/admin/login"
                  data-testid="back-to-login"
                  className="text-sm text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to login
                </Link>
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
}
